import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  Snackbar,
  Stack,
  Collapse,
  CircularProgress,
  Avatar,
} from '@mui/material';
import ImageGenerationModals from './ImageGenerationModals';
import { ContentCopy, Translate, FileCopy } from '@mui/icons-material';
import copyIcon from '@/assets/icons/copy.svg';
import PromptComposer from '@/components/common/PromptComposer';
import translateIcon from '@/assets/icons/translate btn.svg';
import aiIcon from '@/assets/icons/ai.svg';
import reloadIcon from '@/assets/icons/reload.svg';
import downloadIcon from '@/assets/icons/download.svg';
import loadingIcon from '@/assets/icons/loading.svg';
import { Icon } from '@iconify/react';
import { apiClient } from '@/api/api-client';
import CharacterCounter from '@/components/common/CharacterCounter';
import { encrypt2Async } from '@/utils/crypto-utils';

interface ImageMessage {
  id: string;
  type: 'user' | 'ai';
  prompt: string;
  content: string;
  imageUrl?: string;
  timestamp: Date;
  status?: 'generating' | 'completed' | 'error';
  translatedContent?: string;
}

interface CopiedPrompt {
  text: string;
  timestamp: Date;
  source: 'chat' | 'manual';
}

interface SharedTabState {
  // 텍스트 생성 탭 상태 / Text generation tab state
  textPrompt: string;
  textTranslatedPrompt: string;
  textMessages: any[];
  textShowTranslation: boolean;

  // 이미지 생성 탭 상태 / Image generation tab state
  imagePrompt: string;
  imageMessages: any[];
  imageCopiedPrompts: any[];
  imageShowCopiedPrompts: boolean;
}

interface ImageGenerationTabProps {
  sharedState: SharedTabState;
  setSharedState: React.Dispatch<React.SetStateAction<SharedTabState>>;
}

const ImageGenerationTab = ({
  sharedState,
  setSharedState,
}: ImageGenerationTabProps) => {
  // 공유 상태에서 값 가져오기 / Get values from shared state
  const prompt = sharedState.imagePrompt;
  const messages = sharedState.imageMessages;
  const copiedPrompts = sharedState.imageCopiedPrompts;
  const showCopiedPrompts = sharedState.imageShowCopiedPrompts;

  // 로컬 상태 / Local state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showTranslateDialog, setShowTranslateDialog] = useState(false);
  const [translateSource, setTranslateSource] = useState('');
  const [translateResult, setTranslateResult] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isTranslateButtonActive, setIsTranslateButtonActive] = useState(false);
  const [isApplyButtonActive, setIsApplyButtonActive] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false); // 번역 로딩 상태 / Translation loading state

  // 상태 업데이트 함수들 / State update functions
  const setPrompt = (value: string) => {
    setSharedState(prev => ({ ...prev, imagePrompt: value }));
  };

  const setMessages = (value: React.SetStateAction<ImageMessage[]>) => {
    setSharedState(prev => ({
      ...prev,
      imageMessages:
        typeof value === 'function' ? value(prev.imageMessages) : value,
    }));
  };

  const setCopiedPrompts = (value: React.SetStateAction<CopiedPrompt[]>) => {
    setSharedState(prev => ({
      ...prev,
      imageCopiedPrompts:
        typeof value === 'function' ? value(prev.imageCopiedPrompts) : value,
    }));
  };

  const setShowCopiedPrompts = (value: boolean) => {
    setSharedState(prev => ({ ...prev, imageShowCopiedPrompts: value }));
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // [Gemini/Imagen 전환] 한국어 차단 제거 - Imagen 4.0은 한국어 프롬프트 지원
  // const hasHangul = /[\u3131-\uD79D]/.test(prompt);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar 상태 / Snackbar state
  const [snackbarMessage, setSnackbarMessage] = useState(''); // Snackbar 메시지 / Snackbar message

  // 메시지 목록 자동 스크롤 / Auto scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Snackbar 표시 함수 / Show snackbar function
  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };


  // 번역하기 버튼 활성화 상태 업데이트 / Update translate button active state
  useEffect(() => {
    if (showTranslateDialog) {
      setIsTranslateButtonActive(translateSource.trim().length > 0);
    }
  }, [translateSource, showTranslateDialog]);

  // 개발 모드: 로딩 중일 때 progress 증가 / Development mode: Increase progress while loading
  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 2;
        });
      }, 60);
      return () => clearInterval(interval);
    }
  }, [isLoading]);

  // 채팅 탭에서 번역된 프롬프트 복사 / Copy translated prompt from chat tab
  const handleCopyFromChat = () => {
    // 텍스트 생성 탭의 번역된 프롬프트 가져오기 / Get translated prompt from text generation tab
    const chatTranslatedPrompt = sharedState.textTranslatedPrompt;
    if (chatTranslatedPrompt) {
      setPrompt(chatTranslatedPrompt);
      setCopiedPrompts(prev => [
        ...prev,
        {
          text: chatTranslatedPrompt,
          timestamp: new Date(),
          source: 'chat',
        },
      ]);
      setShowCopySuccess(true);
    }
  };

  // 복사된 프롬프트 히스토리에서 선택 / Select from copied prompt history
  const handleSelectFromHistory = (promptText: string) => {
    setPrompt(promptText);
  };

  // 복사 성공 알림 닫기 / Close copy success notification
  const handleCloseCopySuccess = () => {
    setShowCopySuccess(false);
  };

  // 클립보드에서 프롬프트 붙여넣기 / Paste prompt from clipboard
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.trim()) {
        setPrompt(text);
        setCopiedPrompts(prev => [
          ...prev,
          {
            text: text,
            timestamp: new Date(),
            source: 'manual',
          },
        ]);
      }
    } catch (err) {
      setError('클립보드에서 텍스트를 읽을 수 없습니다.');
    }
  };

  // 번역 모달 열기 (플레이스홀더) / Open translate modal (placeholder)
  const handleOpenTranslateModal = () => {
    setShowTranslateDialog(true);
    // 현재 입력된 프롬프트를 번역 소스로 설정 / Set current prompt as translation source
    setTranslateSource(prompt);
    setTranslateResult('');
    // 초기 상태 설정
    setIsTranslateButtonActive(prompt.trim().length > 0);
    setIsApplyButtonActive(false);
  };

  // 번역 모달 닫기 / Close translate modal
  const handleCloseTranslateModal = () => {
    setShowTranslateDialog(false);
    // 모든 값 초기화
    setTranslateSource('');
    setTranslateResult('');
    setIsTranslateButtonActive(false);
    setIsApplyButtonActive(false);
  };

  // 이미지 생성 / Generate image
  const handleGenerateImage = async () => {
    if (isLoading) return;

    // [Gemini/Imagen 전환] 한국어 차단 제거 - Imagen 4.0은 한국어 프롬프트 지원
    // if (hasHangul) {
    //   showSnackbar('영문 프롬프트만 제출 가능합니다');
    //   return;
    // }

    if (!prompt.trim()) return;

    const userMessage: ImageMessage = {
      id: Date.now().toString(),
      type: 'user',
      prompt: prompt,
      content: prompt,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setLoadingProgress(0);
    setError(null);

    try {
      // 사용자 ID 가져오기 (text-generation/stream과 동일하게 처리)
      // Get user ID (same as text-generation/stream)
      // 1. URL 파라미터에서 암호화된 userId 우선 확인 / Check encrypted userId from URL params first
      const urlParams = new URLSearchParams(window.location.search);
      let userId: string | null = urlParams.get('user');
      
      // 2. URL 파라미터에 없는 경우 localStorage에서 가져와서 {이름}_{userId} 형태로 구성 후 암호화
      // If not in URL params, get from localStorage and construct as {name}_{userId} format then encrypt
      if (!userId) {
        const userInfo = localStorage.getItem('kpc_user_info');
        if (userInfo) {
          try {
            const user = JSON.parse(userInfo);
            // {이름}_{userId} 형태로 구성 / Construct as {name}_{userId} format
            if (user.name && user.id) {
              const userIdWithName = `${user.name}_${user.id}`;
              // HTTP 헤더에 한글이 들어갈 수 없으므로 암호화
              // Encrypt because HTTP headers cannot contain Korean characters
              userId = await encrypt2Async(userIdWithName);
            } else if (user.id) {
              // 이름이 없는 경우 ID만 사용 / Use only ID if name is not available
              userId = user.id;
            }
          } catch (error) {
            console.error('사용자 정보 파싱/암호화 실패 / Failed to parse/encrypt user info:', error);
          }
        }
      }

      // expKey 가져오기 / Get expKey from URL params
      const getExpKey = (): string | null => {
        try {
          return urlParams.get('expKey');
        } catch (error) {
          console.error('expKey 가져오기 실패 / Failed to get expKey:', error);
          return null;
        }
      };
      const expKey = getExpKey();

      // 헤더 구성 / Build headers
      const headers: Record<string, string> = {};
      if (userId) {
        headers['userId'] = userId;
      }
      if (expKey) {
        headers['x-experiment-key'] = expKey;
      }

      // 프로그레스 증가 시뮬레이션 / Progress increase simulation
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 2;
        });
      }, 60); // 3초 동안 100% 도달 / Reach 100% in 3 seconds

      // 실제 API 호출 (V2 - Imagen) / Actual API call (V2 - Imagen)
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://ailic.kpc.or.kr';
      const response = await fetch(`${baseUrl}/api/v2/kpc/image-generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({ prompt: prompt.trim() }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // API 응답에서 이미지 URL 추출 / Extract image URL from API response
      const imageUrl = responseData?.data?.imageUrl;

      if (!imageUrl) {
        throw new Error('이미지 URL을 받지 못했습니다.');
      }

      const aiMessage: ImageMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        prompt: prompt,
        content: `이미지가 생성되었습니다: ${prompt}`,
        imageUrl: imageUrl, // 실제 생성된 이미지 URL / Actual generated image URL
        timestamp: new Date(),
        status: 'completed',
      };

      setMessages(prev => [...prev, aiMessage]);
      clearInterval(interval);
    } catch (err) {
      console.error('이미지 생성 실패:', err);
      setError('이미지 생성 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
      setLoadingProgress(0);
      setPrompt('');
    }
  };

  // 이미지 재생성 / Regenerate image
  const handleRegenerateImage = async (promptText: string) => {
    if (isLoading) return;

    // [Gemini/Imagen 전환] 한국어 차단 제거
    // if (hasHangul) {
    //   showSnackbar('영문 프롬프트만 제출 가능합니다');
    //   return;
    // }

    if (!promptText.trim()) return;

    const userMessage: ImageMessage = {
      id: Date.now().toString(),
      type: 'user',
      prompt: promptText,
      content: promptText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setLoadingProgress(0);
    setError(null);

    try {
      // 사용자 ID 가져오기 (text-generation/stream과 동일하게 처리)
      // Get user ID (same as text-generation/stream)
      // 1. URL 파라미터에서 암호화된 userId 우선 확인 / Check encrypted userId from URL params first
      const urlParams = new URLSearchParams(window.location.search);
      let userId: string | null = urlParams.get('user');
      
      // 2. URL 파라미터에 없는 경우 localStorage에서 가져와서 {이름}_{userId} 형태로 구성 후 암호화
      // If not in URL params, get from localStorage and construct as {name}_{userId} format then encrypt
      if (!userId) {
        const userInfo = localStorage.getItem('kpc_user_info');
        if (userInfo) {
          try {
            const user = JSON.parse(userInfo);
            // {이름}_{userId} 형태로 구성 / Construct as {name}_{userId} format
            if (user.name && user.id) {
              const userIdWithName = `${user.name}_${user.id}`;
              // HTTP 헤더에 한글이 들어갈 수 없으므로 암호화
              // Encrypt because HTTP headers cannot contain Korean characters
              userId = await encrypt2Async(userIdWithName);
            } else if (user.id) {
              // 이름이 없는 경우 ID만 사용 / Use only ID if name is not available
              userId = user.id;
            }
          } catch (error) {
            console.error('사용자 정보 파싱/암호화 실패 / Failed to parse/encrypt user info:', error);
          }
        }
      }

      // expKey 가져오기 / Get expKey from URL params
      const getExpKey = (): string | null => {
        try {
          return urlParams.get('expKey');
        } catch (error) {
          console.error('expKey 가져오기 실패 / Failed to get expKey:', error);
          return null;
        }
      };
      const expKey = getExpKey();

      // 헤더 구성 / Build headers
      const headers: Record<string, string> = {};
      if (userId) {
        headers['userId'] = userId;
      }
      if (expKey) {
        headers['x-experiment-key'] = expKey;
      }

      // 프로그레스 증가 시뮬레이션 / Progress increase simulation
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 2;
        });
      }, 60); // 3초 동안 100% 도달 / Reach 100% in 3 seconds

      // 실제 API 호출 (V2 - Imagen) / Actual API call (V2 - Imagen)
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://ailic.kpc.or.kr';
      const response = await fetch(`${baseUrl}/api/v2/kpc/image-generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({ prompt: promptText.trim() }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // API 응답에서 이미지 URL 추출 / Extract image URL from API response
      const imageUrl = responseData?.data?.imageUrl;

      if (!imageUrl) {
        throw new Error('이미지 URL을 받지 못했습니다.');
      }

      const aiMessage: ImageMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        prompt: promptText,
        content: `이미지가 생성되었습니다: ${promptText}`,
        imageUrl: imageUrl, // 실제 생성된 이미지 URL / Actual generated image URL
        timestamp: new Date(),
        status: 'completed',
      };

      setMessages(prev => [...prev, aiMessage]);
      clearInterval(interval);
    } catch (err) {
      console.error('이미지 생성 실패:', err);
      setError('이미지 생성 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
      setLoadingProgress(0);
      // 입력창은 비우지 않음 (재생성이므로)
    }
  };

  // 프롬프트 복사 / Copy prompt
  const handleCopyPrompt = (text: string) => {
    navigator.clipboard.writeText(text);
    setShowCopySuccess(true);
  };

  // 이미지 선택 및 확대 보기 / Select and view image
  const handleImageSelect = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageDialog(true);
  };

  // 파일 선택 핸들러 / File selection handler
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setSelectedFiles(prev => [...prev, ...newFiles]);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 파일 삭제 핸들러 / File removal handler
  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Box
      sx={{
        height: '75vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        position: 'relative', // 절대 위치 요소를 위한 기준점 설정
      }}
    >
      {/* 에러 알림 / Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 복사 성공 알림 / Copy Success Notification */}
      <Snackbar
        open={showCopySuccess}
        autoHideDuration={2000}
        onClose={handleCloseCopySuccess}
        message="텍스트가 복사되었습니다"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* 영문 전용 경고 Snackbar / English Only Warning Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        style={{ bottom: '130px' }}
      >
        <Box
          sx={{
            bgcolor: 'rgba(38,44,51,0.7)',
            color: 'white',
            px: 3,
            py: 2,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: '300px',
            justifyContent: 'center',
          }}
        >
          <Icon icon="ep:warning" color="#FFFFFF" width={24} height={24} />
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            {snackbarMessage}
          </Typography>
        </Box>
      </Snackbar>

      {/* 메인 이미지 생성 영역 / Main Image Generation Area */}
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          // border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* 메인 컨텐츠 영역 / Main Content Area */}
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* 메시지 목록 / Messages List */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ lineHeight: '22px' }}
                >
                  이미지 생성을 위한 프롬프트를 입력해 주세요.
                </Typography>
              </Box>
            ) : (
              messages.map(message => (
                <Box key={message.id}>
                  {message.type === 'user' && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Card
                        elevation={0}
                        sx={{
                          maxWidth: '70%',
                          bgcolor: 'grey.200',
                          color: 'text.primary',
                          borderRadius: '18px',
                          border: 'none',
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap' }}
                          >
                            {message.prompt}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mt: 0,
                        }}
                      >
                        <Tooltip title="텍스트 복사" arrow placement="top">
                          <IconButton
                            size="small"
                            className="action-button"
                            onClick={() => handleCopyPrompt(message.prompt)}
                            sx={{ p: 0 }}
                          >
                            <img
                              src={copyIcon}
                              alt="Copy"
                              style={{
                                width: 20,
                                height: 20,
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}

                  {message.type === 'ai' && message.imageUrl && (
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.75,
                          mb: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: 'transparent',
                            width: 32,
                            height: 32,
                            mt: 0.5, // 텍스트와 정렬 맞추기
                          }}
                        >
                          <img
                            src={aiIcon}
                            alt="AI"
                            style={{
                              width: 32,
                              height: 32,
                            }}
                          />
                        </Avatar>
                        <Box sx={{ flex: 1, mt:1.3, ml: 1 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            이미지 생성이 완료되었습니다.
                          </Typography>
                          <Box
                            component="img"
                            src={message.imageUrl}
                            alt="Generated image"
                            sx={{
                              cursor: 'pointer',
                              maxWidth: '326px',
                              width: '100%',
                              height: 'auto',
                              borderRadius: 2,
                            }}
                            onClick={() => handleImageSelect(message.imageUrl!)}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              gap: '5px',
                              mt: 1,
                            }}
                          >
                          <Tooltip title="다운로드" arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() => {
                                try {
                                  // 백엔드 프록시 엔드포인트를 통해 다운로드
                                  const proxyUrl = `${import.meta.env.VITE_API_BASE_URL || 'https://ailic.kpc.or.kr'}/api/v2/kpc/download-image?url=${encodeURIComponent(message.imageUrl!)}`;
                                  
                                  const link = document.createElement('a');
                                  link.href = proxyUrl;
                                  link.download = `generated-image-${Date.now()}.jpg`;
                                  link.style.display = 'none';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                } catch (error) {
                                  console.error('다운로드 실패:', error);
                                }
                              }}
                              sx={{ p: 0 }}
                            >
                              <img
                                src={downloadIcon}
                                alt="Download"
                                style={{
                                  width: 20,
                                  height: 20,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="다시 생성" arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() => {
                                // 리로드 기능 - 같은 프롬프트로 바로 재생성 / Reload - regenerate with same prompt immediately
                                handleRegenerateImage(message.prompt);
                              }}
                              sx={{ p: 0 }}
                            >
                              <img
                                src={reloadIcon}
                                alt="Reload"
                                style={{
                                  width: 20,
                                  height: 20,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))
            )}

            {/* 로딩 인디케이터 / Loading Indicator */}
            {isLoading && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'transparent',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <img
                      src={aiIcon}
                      alt="AI"
                      style={{
                        width: 32,
                        height: 32,
                      }}
                    />
                  </Avatar>
                  <Typography variant="body2">
                    이미지를 생성하는 중입니다. 잠시만 기다려주세요.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '326px',
                    height: '328px',
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ml: 5,
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={40}
                      thickness={2}
                      sx={{
                        color: 'grey.300',
                        '& .MuiCircularProgress-circle': {
                          strokeLinecap: 'round',
                        },
                      }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={loadingProgress}
                      size={40}
                      thickness={2}
                      sx={{
                        color: 'grey.900',
                        position: 'absolute',
                        left: 0,
                        '& .MuiCircularProgress-circle': {
                          strokeLinecap: 'round',
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={loadingIcon}
                        alt="Loading"
                        style={{
                          width: 20,
                          height: 20,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* 입력 영역 / Input Area */}
          <Box sx={{ p: 0, position: 'relative' }}>

            <Stack spacing={0}>
              {/* 복사된 프롬프트 히스토리 / Copied Prompts History */}
              <Collapse in={showCopiedPrompts}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    복사된 프롬프트 히스토리
                  </Typography>
                  {copiedPrompts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      아직 복사된 프롬프트가 없습니다.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {copiedPrompts
                        .slice(-5)
                        .reverse()
                        .map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {item.text.substring(0, 100)}
                              {item.text.length > 100 ? '...' : ''}
                            </Typography>
                            <Chip
                              label={item.source === 'chat' ? '채팅' : '수동'}
                              size="small"
                              color={
                                item.source === 'chat' ? 'primary' : 'secondary'
                              }
                            />
                            <Button
                              size="small"
                              onClick={() => handleSelectFromHistory(item.text)}
                            >
                              선택
                            </Button>
                          </Box>
                        ))}
                    </Stack>
                  )}
                </Paper>
              </Collapse>

              {/* 선택된 파일 목록 / Selected Files List */}
              {selectedFiles.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'background.paper',
                  }}
                >
                  {selectedFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      onDelete={() => handleRemoveFile(index)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              )}

              {/* 문자 카운터 - 입력창 위 div / Character counter - Above input div */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mb: 1,
                  px: 1.5,
                }}
              >
                <CharacterCounter
                  currentCount={prompt.length}
                  maxCount={1024}
                  label="자"
                />
              </Box>

              {/* 프롬프트 입력창과 액션 버튼들 / Prompt Input and Action Buttons */}
              {/* 숨겨진 파일 입력 / Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <PromptComposer
                value={prompt}
                onChange={setPrompt}
                onSend={handleGenerateImage}
                isSending={isLoading}
                maxCharacters={1024}
                showCharacterCount={false}
                inputPadding={'20px'}
                placeholder="이미지 생성을 위한 프롬프트를 입력하세요."
                /* [Gemini/Imagen 전환] 번역 기능 주석 처리 - Imagen 4.0은 한국어 지원
                leftAction={{
                  type: 'custom',
                  icon: translateIcon,
                  label: '번역',
                  onClick: handleOpenTranslateModal,
                  active: hasHangul,
                  color: 'primary.main',
                }}
                */
                rightActions={
                  <Stack direction="row" spacing={1} sx={{ mb: 0.25 }}>
                    <Button
                      variant="outlined"
                      startIcon={<FileCopy />}
                      onClick={handlePasteFromClipboard}
                      size="small"
                      sx={{ minHeight: '24px' }}
                    >
                      붙여넣기
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Translate />}
                      onClick={handleCopyFromChat}
                      size="small"
                      sx={{ minHeight: '24px' }}
                    >
                      채팅복사
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setShowCopiedPrompts(!showCopiedPrompts)}
                      size="small"
                      sx={{ minHeight: '24px' }}
                    >
                      히스토리
                    </Button>
                    <Tooltip title="프롬프트 복사">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyPrompt(prompt)}
                        sx={{ p: 0 }}
                      >
                        <ContentCopy sx={{ width: 20, height: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              />
            </Stack>
          </Box>
        </Box>
      </Paper>

      {/* 모달 컴포넌트들 / Modal Components */}
      <ImageGenerationModals
        showImageDialog={showImageDialog}
        setShowImageDialog={setShowImageDialog}
        selectedImage={selectedImage}
        /* [Gemini/Imagen 전환] 번역 모달 관련 props 주석 처리 */
        showTranslateDialog={false}
        setShowTranslateDialog={() => {}}
        translateSource={''}
        setTranslateSource={() => {}}
        translateResult={''}
        setTranslateResult={() => {}}
        onTranslateClick={async () => {}}
        onApplyClick={() => {}}
        onCloseTranslateModal={() => {}}
        isTranslateButtonActive={false}
        isApplyButtonActive={false}
        isTranslating={false}
      />
    </Box>
  );
};

export default ImageGenerationTab;
